import React, { useEffect, useState, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '../../auth/AuthContext';


const API_URL = process.env.REACT_APP_API_URL;

/** helper: parsowanie liczb z PL przecinkiem */
const toNum = (v, digits = 2) => {
  if (v === '' || v === null || typeof v === 'undefined') return null;
  const n = Number(String(v).replace(',', '.'));
  if (Number.isNaN(n)) return null;
  return Number(n.toFixed(digits));
};

const ListaTowarowUslug = () => {
  const { hasPermission, hasAnyPermission } = useAuth();
  const canViewWarehouseItems = hasAnyPermission(['warehouse_items_view', 'warehouse_view', 'warehouse_manage']);
  const hideCostsMargin = hasPermission('hide_costs_margin');
  const hideSalePrices = hasPermission('hide_sale_prices');
  const canCreateWarehouseItems = hasAnyPermission(['warehouse_items_create', 'warehouse_manage']) && !hideCostsMargin;
  const canEditWarehouseItems = hasAnyPermission(['warehouse_items_edit', 'warehouse_manage']) && !hideCostsMargin;
  const canDeleteWarehouseItems = hasAnyPermission(['warehouse_items_delete', 'warehouse_manage']);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);

  // słowniki
  const [kategorie, setKategorie] = useState([]);
  const [jednostki, setJednostki] = useState([]);
  const [stawkiVAT, setStawkiVAT] = useState([]);

  // dodawanie
  const [isAdding, setIsAdding] = useState(false);
  const [newItem, setNewItem] = useState({
    kategoria_id: '',
    nazwa: '',
    jednostka_id: '',
    koszt_netto: '',
    marza_proc: '',
    vat_id: "",
    vat_proc: "",
    vat_nazwa: "",
    producent: '',
    model: '',
    opis: ''
  });

  // edycja
  const [editingId, setEditingId] = useState(null);
  const [edit, setEdit] = useState({});

  const tokenHeader = useMemo(() => {
    const token = localStorage.getItem('token');
    return { Authorization: `Bearer ${token}` };
  }, []);

  const fetchSlowniki = async () => {
    const [k, j, v] = await Promise.all([
      axios.get(`${API_URL}/towary-uslugi/slowniki/kategorie/lista`, { headers: tokenHeader }),
      axios.get(`${API_URL}/towary-uslugi/slowniki/jednostki/lista`, { headers: tokenHeader }),
      axios.get(`${API_URL}/towary-uslugi/slowniki/stawki-vat/lista`, { headers: tokenHeader }),
    ]);
    setKategorie(k.data || []);
    setJednostki(j.data || []);
    setStawkiVAT(v.data || []);
  };

  const fetchRows = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/towary-uslugi?active=true&limit=500`, {
        headers: tokenHeader,
      });
      setRows(res.data || []);
    } catch (e) {
      console.error('Błąd pobierania towarów/usług', e);
      alert('Nie udało się pobrać listy towarów/usług.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSlowniki().then(fetchRows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ——— Handlery dodawania
  const handleNewChange = (e) => {
    const { name, value } = e.target;
    setNewItem((p) => ({ ...p, [name]: value }));
  };

  function handleVatChange(e) {
    const id = e.target.value || "";
    const entry = stawkiVAT.find(v => String(v.id) === String(id)) || null;

    setNewItem(prev => ({
      ...prev,
      vat_id: entry?.id ?? "",
      vat_proc: entry ? Number(entry.proc) : "",  // to pole pójdzie na serwer
      vat_nazwa: entry?.nazwa ?? "",
    }));
  }

  const handleAdd = async () => {
    if (!canCreateWarehouseItems) return;
    try {
      const payload = {
        kategoria_id: newItem.kategoria_id || null,
        nazwa: newItem.nazwa?.trim(),
        jednostka_id: newItem.jednostka_id || null,
        koszt_netto: toNum(newItem.koszt_netto, 2),
        marza_proc: newItem.marza_proc === '' ? null : toNum(newItem.marza_proc, 2),
        vat_proc: toNum(newItem.vat_proc, 2),
        producent: newItem.producent?.trim() || null,
        model: newItem.model?.trim() || null,
        opis: newItem.opis?.trim() || null,
        active: true
      };

      if (!payload.kategoria_id || !payload.nazwa || !payload.jednostka_id || payload.koszt_netto === null || payload.vat_proc === null) {
        alert('Wypełnij: Kategoria, Nazwa, Jednostka, Koszt netto, VAT.');
        return;
      }

      await axios.post(`${API_URL}/towary-uslugi`, payload, { headers: tokenHeader });
      await fetchRows();
      setIsAdding(false);
      setNewItem({
        kategoria_id: '',
        nazwa: '',
        jednostka_id: '',
        koszt_netto: '',
        marza_proc: '',
        vat_proc: '',
        producent: '',
        model: '',
        opis: ''
      });
    } catch (e) {
      console.error('Błąd dodawania pozycji', e);
      alert(e.response?.data?.error || 'Nie udało się dodać pozycji.');
    }
  };

  // ——— Handlery edycji
  const beginEdit = (row) => {
    if (!canEditWarehouseItems) return;
    setEditingId(row.id);
    setEdit({
      kategoria_id: row.kategoria_id,
      nazwa: row.nazwa,
      jednostka_id: row.jednostka_id,
      koszt_netto: row.koszt_netto,
      marza_proc: row.marza_proc ?? '',
      vat_proc: row.vat_proc,
      producent: row.producent || '',
      model: row.model || '',
      opis: row.opis || ''
    });
  };
  const cancelEdit = () => {
    setEditingId(null);
    setEdit({});
  };
function handleEditChange(e) {
  const { name, value } = e.target;

  if (name === "vat_id") {
    const entry =
      stawkiVAT.find((v) => String(v.id) === String(value)) || null;

    setEdit((prev) => ({
      ...prev,
      vat_id: entry?.id ?? "",
      vat_proc: entry ? Number(entry.proc) : "",  // to wyślesz na serwer
      vat_nazwa: entry?.nazwa ?? "",
    }));
  } else {
    setEdit((prev) => ({ ...prev, [name]: value }));
  }
}


  const saveEdit = async (id) => {
    if (!canEditWarehouseItems) return;
    try {
      const payload = {
        kategoria_id: edit.kategoria_id || null,
        nazwa: edit.nazwa?.trim(),
        jednostka_id: edit.jednostka_id || null,
        koszt_netto: toNum(edit.koszt_netto, 2),
        marza_proc: edit.marza_proc === '' ? null : toNum(edit.marza_proc, 2),
        vat_proc: toNum(edit.vat_proc, 2),
        producent: edit.producent?.trim() || null,
        model: edit.model?.trim() || null,
        opis: edit.opis?.trim() || null
      };

      await axios.put(`${API_URL}/towary-uslugi/${id}`, payload, { headers: tokenHeader });
      await fetchRows();
      cancelEdit();
    } catch (e) {
      console.error('Błąd aktualizacji pozycji', e);
      alert(e.response?.data?.error || 'Nie udało się zaktualizować pozycji.');
    }
  };

  // ——— Archiwizacja (soft delete)
  const archiveRow = async (id) => {
    if (!canDeleteWarehouseItems) return;
    if (!window.confirm('Zarchiwizować tę pozycję?')) return;
    try {
      await axios.delete(`${API_URL}/towary-uslugi/${id}`, { headers: tokenHeader });
      fetchRows();
    } catch (e) {
      console.error('Błąd archiwizacji', e);
      alert('Nie udało się zarchiwizować pozycji.');
    }
  };

  // ——— pomocnicze mapowania nazw
  const kategoriaName = (id) => kategorie.find((k) => k.id === id)?.nazwa || '—';
  const jednostkaSymbol = (id) => jednostki.find((j) => j.id === id)?.symbol || '—';

  return (
    <div className="lista-urzadzen-container">
      <div className="header">
        <h2>Towary / Usługi</h2>
        {canCreateWarehouseItems ? <button className="add-button" onClick={() => setIsAdding(true)}>Dodaj nowe</button> : null}
      </div>

      {!canViewWarehouseItems ? (
        <p>Brak uprawnień do wyświetlania elementów magazynowych.</p>
      ) : null}

      {/* akcje dla nowego wiersza — jak w Twoim komponencie */}
      {isAdding && canCreateWarehouseItems && canViewWarehouseItems && (
        <div className="new-device-actions">
          <button onClick={handleAdd}>Zapisz</button>
          <button onClick={() => { setIsAdding(false); setNewItem({
            kategoria_id: '', nazwa: '', jednostka_id: '',
            koszt_netto: '', marza_proc: '', vat_proc: '',
            producent: '', model: '', opis: ''
          }); }}>
            Anuluj
          </button>
        </div>
      )}

      <table className="lista-urzadzen-table">
        <thead>
          <tr>
            <th>Kategoria</th>
            <th>Nazwa</th>
            <th>J.m.</th>
            {!hideCostsMargin ? <th>Koszt netto</th> : null}
            {!hideCostsMargin ? <th>Marża %</th> : null}
            {!hideSalePrices ? <th>Cena sprzedaży netto</th> : null}
            <th>VAT %</th>
            <th>Producent</th>
            <th>Model</th>
            <th>Akcje</th>
          </tr>
        </thead>
        <tbody>
          {/* wiersz dodawania */}
          {isAdding && canViewWarehouseItems && (
            <tr className="new-device-row">
              
              <td>
                <select name="kategoria_id" value={newItem.kategoria_id} onChange={handleNewChange}>
                  <option value="">— wybierz —</option>
                  {kategorie.map((k) => (
                    <option key={k.id} value={k.id}>{k.nazwa}</option>
                  ))}
                </select>
              </td>
              <td>
                <input type="text" name="nazwa" value={newItem.nazwa} onChange={handleNewChange} placeholder="Nazwa" />
              </td>
              <td>
                <select name="jednostka_id" value={newItem.jednostka_id} onChange={handleNewChange}>
                  <option value="">— wybierz —</option>
                  {jednostki.map((j) => (
                    <option key={j.id} value={j.id}>{j.symbol}{j.nazwa ? ` (${j.nazwa})` : ''}</option>
                  ))}
                </select>
              </td>
              {!hideCostsMargin ? (
                <td>
                  <input type="number" step="0.01" name="koszt_netto" value={newItem.koszt_netto} onChange={handleNewChange} placeholder="0.00" />
                </td>
              ) : null}
              {!hideCostsMargin ? (
                <td>
                  <input type="number" step="0.01" name="marza_proc" value={newItem.marza_proc} onChange={handleNewChange} placeholder="np. 25" />
                </td>
              ) : null}
              {!hideSalePrices ? (
                <td>
                  {(() => {
                    const koszt = Number(String(newItem.koszt_netto || '').replace(',', '.'));
                    const marza = Number(String(newItem.marza_proc || '').replace(',', '.'));
                    if (!Number.isFinite(koszt)) return '—';
                    const cena = Number.isFinite(marza) ? koszt * (1 + marza / 100) : koszt;
                    return Number(cena).toFixed(2);
                  })()}
                </td>
              ) : null}
              <td>
                <select
                  name="vat_id"
                  value={String(newItem.vat_id ?? "")}
                  onChange={handleVatChange}
                >
                  <option value="">— wybierz —</option>
                  {stawkiVAT.map(v => (
                    <option key={v.id} value={String(v.id)}>
                      {v.nazwa}
                    </option>
                  ))}
                </select>
              </td>
              <td>
                <input type="text" name="producent" value={newItem.producent} onChange={handleNewChange} placeholder="Producent" />
              </td>
              <td>
                <input type="text" name="model" value={newItem.model} onChange={handleNewChange} placeholder="Model" />
              </td>
              <td />
            </tr>
          )}

          {/* rekordy */}
          {!canViewWarehouseItems ? (
            <tr><td colSpan="10">Brak uprawnień</td></tr>
          ) : loading ? (
            <tr><td colSpan="10">Ładowanie…</td></tr>
          ) : rows.length === 0 ? (
            <tr><td colSpan="10">Brak danych</td></tr>
          ) : (
            rows.map((r) => (
              <tr key={r.id}>
                

                {editingId === r.id ? (
                  <>
                    <td>
                      <select name="kategoria_id" value={edit.kategoria_id} onChange={handleEditChange}>
                        {kategorie.map((k) => (
                          <option key={k.id} value={k.id}>{k.nazwa}</option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input type="text" name="nazwa" value={edit.nazwa} onChange={handleEditChange} />
                    </td>
                    <td>
                      <select name="jednostka_id" value={edit.jednostka_id} onChange={handleEditChange}>
                        {jednostki.map((j) => (
                          <option key={j.id} value={j.id}>{j.symbol}{j.nazwa ? ` (${j.nazwa})` : ''}</option>
                        ))}
                      </select>
                    </td>
                    {!hideCostsMargin ? (
                      <td>
                        <input type="number" step="0.01" name="koszt_netto" value={edit.koszt_netto} onChange={handleEditChange} />
                      </td>
                    ) : null}
                    {!hideCostsMargin ? (
                      <td>
                        <input type="number" step="0.01" name="marza_proc" value={edit.marza_proc} onChange={handleEditChange} />
                      </td>
                    ) : null}
                    {!hideSalePrices ? (
                      <td>
                        {(() => {
                          const koszt = Number(edit.koszt_netto);
                          const marza = Number(edit.marza_proc);
                          if (!Number.isFinite(koszt)) return '—';
                          const cena = Number.isFinite(marza) ? koszt * (1 + marza / 100) : koszt;
                          return Number(cena).toFixed(2);
                        })()}
                      </td>
                    ) : null}
                    <td>
                      <select
                        name="vat_id"
                        value={String(edit.vat_id ?? "")}
                        onChange={handleEditChange}   // patrz handler poniżej
                      >
                        <option value="">— wybierz —</option>
                        {stawkiVAT.map((v) => (
                          <option key={v.id} value={String(v.id)}>
                            {v.nazwa}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>
                      <input type="text" name="producent" value={edit.producent} onChange={handleEditChange} />
                    </td>
                    <td>
                      <input type="text" name="model" value={edit.model} onChange={handleEditChange} />
                    </td>
                    <td>
                      {canEditWarehouseItems ? <button onClick={() => saveEdit(r.id)}>Zapisz</button> : null}
                      {canEditWarehouseItems ? <button onClick={cancelEdit}>Anuluj</button> : null}
                    </td>
                  </>
                ) : (
                  <>
                    <td>{kategoriaName(r.kategoria_id)}</td>
                    <td>{r.nazwa}</td>
                    <td>{jednostkaSymbol(r.jednostka_id)}</td>
                    {!hideCostsMargin ? <td>{Number(r.koszt_netto).toFixed(2)}</td> : null}
                    {!hideCostsMargin ? <td>{r.marza_proc !== null ? Number(r.marza_proc).toFixed(2) : '—'}</td> : null}
                    {!hideSalePrices ? (
                      <td>
                        {(() => {
                          const koszt = Number(r.koszt_netto);
                          const marza = Number(r.marza_proc);
                          if (!Number.isFinite(koszt)) return '—';
                          const cena = Number.isFinite(marza) ? koszt * (1 + marza / 100) : koszt;
                          return Number(cena).toFixed(2);
                        })()}
                      </td>
                    ) : null}
                    <td>{Number(r.vat_proc).toFixed(2)}</td>
                    <td>{r.producent || '—'}</td>
                    <td>{r.model || '—'}</td>
                    <td>
                      {canEditWarehouseItems ? <button onClick={() => beginEdit(r)}>Edytuj</button> : null}
                      {canDeleteWarehouseItems ? <button onClick={() => archiveRow(r.id)}>Archiwizuj</button> : null}
                    </td>
                  </>
                )}
              </tr>
            ))
          )}
        </tbody>
      </table>

      
    </div>
  );
};

export default ListaTowarowUslug;
